/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  buildSwagUpCatalogUrl,
  LOCAL_STORAGE_KEYS,
} from "@command-center-app/constants/common.const";
import { productDetailMappings } from "@command-center-app/helpers/data.tsx";
import { UserInfo } from "@command-center-app/index";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { primaryContactDetails } from "./common.util";
import { VENDOR_SHIPPING_ADDRESS_LABELS } from "@command-center-app/constants/vendorAddress.const";
import { getShippingAddress, getShippingAptSte } from "@command-center-app/utils/vendorAddress";
import { ADD_TO_CATALOG_REQUIRES_ACTIVE_PRODUCT_MESSAGE } from "./product.util";
export const handleScrollLeft = (
  element: any,
  _speed: any,
  _distance: any,
  step: any,
) => {
  let scrollAmount = 0;
  element.scrollLeft -= step;
  scrollAmount -= Math.abs(step);
};

export const handleScroll = (
  element: any,
  _speed: any,
  _distance: any,
  step: any,
) => {
  let scrollAmount = 0;
  element.scrollLeft += step;
  scrollAmount += Math.abs(step);
};

export const handleDownload = (url: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const emailWithAllowedDomains = (message = "Invalid email") =>
  Yup.string().test(
    "is-valid-email",
    message,
    (value: any) =>
      !value ||
      (/^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}$/i.test(value) &&
        Yup.string().email().isValidSync(value)),
  );

function convertStatus(value: any) {
  return value?.toUpperCase().replace(/-/g, "_");
}

export const ucwords = (str: string): string =>
  str?.replace(/\b\w/g, (char) => char?.toUpperCase()) || str;

export const getUserInfo = (): any | null => {
  const userDetails = localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DATA);

  if (!userDetails) return null;

  try {
    const parsed: UserInfo = JSON.parse(userDetails);
    return parsed?.data?.data || null;
  } catch (error) {
    console.error("Failed to parse user-info from localStorage:", error);
    return null;
  }
};

export const getSingleLineAddress = (addresses?: any[]): string => {
  if (!Array.isArray(addresses) || addresses.length === 0) return "";

  const address = addresses[0];
  if (!address) return "";

  const {
    addressLine1,
    addressLine2,
    addressLine3,
    city,
    state,
    country,
    zipcode,
  } = address;

  return [
    addressLine1,
    addressLine2,
    addressLine3,
    city,
    state,
    country,
    zipcode,
  ]
    .filter((part) => part && part.toString().trim() !== "")
    .join(", ");
};

type VendorDetailsType = any;
type StatusOption = { label: string; value: string };
type InventoryStatusOption = { label: string; value: string };
export const transformLeftVendorDetailSection = (
  vendorDetails: VendorDetailsType,
  vendorStatusList: StatusOption[] = [],
) => {
  const _contactInfo = primaryContactDetails(
    vendorDetails?.profile?.contact || [],
  );
  return [
    {
      label: "ACCOUNT OWNER",
      name: "accountOwner",
      type: "select",
      value:
        vendorDetails?.owner?.firstName && vendorDetails?.owner?.lastName
          ? `${vendorDetails.owner.firstName} ${vendorDetails.owner.lastName}`
          : "",
      options: [
        { label: "James", value: "james" },
        { label: "Kaitlyn", value: "kaitlyn" },
      ],
      disabled: false,
      canEdit: false,
    },
    {
      label: "Status",
      name: "status",
      type: "select",
      value: vendorDetails?.status,
      options: vendorStatusList || [],
      disabled: false,
      canEdit: false,
    },
    {
      label: "Company Info",
      name: "companyInfo",
      type: "list",
      canEdit: true,
      data: [
        {
          key: "BDA Supplier Number",
          value: vendorDetails?.profile?.company?.bdaSupplierNumber || "",
          canCopy: true,
        },
        {
          key: "Supplier Site Name",
          value: vendorDetails?.profile?.company?.supplierSiteName || "",
          canCopy: true,
        },
        {
          key: "Supplier Site ID",
          value: vendorDetails?.profile?.company?.supplierSiteId || "",
          canCopy: true,
        },
        {
          key: "Website",
          value: vendorDetails?.profile?.company?.website || "",
          canCopy: true,
        },
        {
          key: "Phone number",
          value: _contactInfo?.phone || "",
          canCopy: true,
        },
        {
          key: "Email",
          value: _contactInfo?.email || "",
          canCopy: true,
        },
        ...(() => {
          const shippingAddress = getShippingAddress(
            vendorDetails?.profile?.company?.addresses,
          );
          return (
            [
              "addressLine1",
              "addressLine2",
              "city",
              "state",
              "country",
              "zipcode",
            ] as const
          ).map((field) => ({
            key: VENDOR_SHIPPING_ADDRESS_LABELS[field],
            value:
              field === "addressLine2"
                ? getShippingAptSte(shippingAddress)
                : shippingAddress?.[field] || "",
            canCopy: false,
          }));
        })(),
        {
          key: "Account type",
          value: vendorDetails?.type?.join(", ") || "",
          canCopy: false,
        },
      ],
    },
    {
      label: "Turnaround Time",
      name: "turnaroundTime",
      type: "list",
      canEdit: true,
      data: [
        {
          key: "Blank regular",
          value: vendorDetails?.standardProductionTurnAround || "N/A",
          canCopy: false,
        },
        {
          key: "Decoration regular",
          value: vendorDetails?.standardDecorationTurnAround || "N/A",
          canCopy: false,
        },
      ],
    },
    {
      label: "Rush Production",
      name: "rushProduction",
      type: "list",
      canEdit: false,
      data: [
        {
          key: "Offers rush production",
          value: vendorDetails?.rushProduction ? "Yes" : "No",
          canCopy: false,
        },
        {
          key: "Blank rush",
          value: vendorDetails?.rushProductionTurnAround || "N/A",
          canCopy: false,
        },
        {
          key: "Decoration rush",
          value: vendorDetails?.rushDecorationTurnAround || "N/A",
          canCopy: false,
        },
      ],
    },
  ];
};

type ServiceDetailsType = any;
export const transformLeftServiceDetailSection = (
  serviceDetails: ServiceDetailsType,
) => {
  const _contactInfo = primaryContactDetails(
    serviceDetails?.profile?.contact || [],
  );
  return [
    {
      label: "ACCOUNT OWNER",
      name: "accountOwner",
      type: "select",
      value:
        serviceDetails?.owner?.firstName && serviceDetails?.owner?.lastName
          ? `${serviceDetails.owner.firstName} ${serviceDetails.owner.lastName}`
          : "James Degnan",
      options: [
        { label: "James", value: "james" },
        { label: "Kaitlyn", value: "kaitlyn" },
      ],
      disabled: false,
      canEdit: false,
    },
    {
      label: "Status",
      name: "status",
      type: "select",
      value: serviceDetails?.status,
      options: [],
      disabled: false,
      canEdit: false,
    },
    {
      label: "Company Info",
      name: "companyInfo",
      type: "list",
      canEdit: true,
      data: [
        {
          key: "Website",
          value: serviceDetails?.profile?.company?.website || "www.sanmar.com",
          canCopy: true,
        },
        {
          key: "Email",
          value: _contactInfo?.email || "mikekelly@sanmar.com",
          canCopy: true,
        },
        {
          key: "Address",
          value:
            getSingleLineAddress(serviceDetails?.profile?.company?.addresses) ||
            "234 Street Address, Los Angeles,90123",
          canCopy: false,
        },
        {
          key: "Account type",
          value: serviceDetails?.type?.join(", ") || "Supplier, Decorator",
          canCopy: false,
        },
      ],
    },
    {
      label: "Turnaround Time",
      name: "turnaroundTime",
      type: "list",
      canEdit: true,
      data: [
        {
          key: "Blank regular",
          value: serviceDetails?.standardProductionTurnAround || "20 days",
          canCopy: false,
        },
        {
          key: "Decoration regular",
          value: serviceDetails?.standardDecorationTurnAround || "20 days",
          canCopy: false,
        },
        {
          key: "Blank rush",
          value: serviceDetails?.rushProductionTurnAround || "20 days",
          canCopy: false,
        },
        {
          key: "Decoration rush",
          value: serviceDetails?.rushDecorationTurnAround || "20 days",
          canCopy: false,
        },
      ],
    },
  ];
};

type ProductDetailsType = any;
export const transformLeftProductDetailSection = (
  productDetails: ProductDetailsType,
  productStatusList: StatusOption[] = [],
  productInventoryStatusList: InventoryStatusOption[],
  catalogDetails?: any,
  isCatalogPage: boolean = false,
) => {
  const isCatalogExists = !!catalogDetails;

  const sections: any[] = [
    {
      label: "Product Status",
      name: "status",
      type: "select",
      value: convertStatus(productDetails && productDetails?.status),
      options: productStatusList || [],
      disabled: false,
    },
    {
      label: "Inventory Status",
      name: "inventoryStatus",
      type: "select",
      value: convertStatus(productDetails && productDetails?.inventoryStatus),
      options: productInventoryStatusList || [],
      disabled: false,
    },
    {
      label: "Supplier Inventory URL *",
      name: "offeringPublicUrl",
      type: "text",
      value: productDetails?.offeringPublicUrl,
      disabled: false,
    },
  ];

  if (isCatalogPage) {
    sections.push({
      label: "SwagUp Catalog URL",
      name: "swagupCatalogUrl",
      type: "externalUrl",
      value:
        productDetails?.status?.toLowerCase() === "active"
          ? buildSwagUpCatalogUrl(
              productDetails?.name,
              productDetails?.productCode,
            )
          : "",
      fallbackText: "Not active",
      disabled: true,
    } as any);
  }

  if (!isCatalogPage) {
    const isAddToCatalogDisabled =
      productDetails?.status?.toLowerCase() !== "active";

    sections.push({
      label: "Add to Catalog",
      name: "addToCatalog",
      type: "checkbox",
      value:
        isCatalogExists &&
        catalogDetails.status?.toLowerCase() === "active",
      disabled: isAddToCatalogDisabled,
      disabledTooltip: isAddToCatalogDisabled
        ? ADD_TO_CATALOG_REQUIRES_ACTIVE_PRODUCT_MESSAGE
        : undefined,
      link:
        isCatalogExists && catalogDetails.status?.toLowerCase() !== "deleted"
          ? `/marketplace/catalog/${catalogDetails._id}`
          : null,
    } as any);
  }


  sections.push({
    label: "Product Thumbnail *",
    name: "thumbnail",
    type: "image",
    value: getProductMediaUrl(productDetails?.media, true, "product"),
    disabled: true,
  } as any);

  sections.push({
    label: "Created By",
    name: "createdBy",
    type: "text",
    value: productDetails?.createdBy,
    disabled: true,
  } as any);

  return sections;
};

export const transformLeftCatalogDetailSection = (
  catalogDetails: ProductDetailsType,
  catalogStatusList: StatusOption[] = [],
  catalogInventoryStatusList: InventoryStatusOption[],
) => {
  return [
    {
      label: "Product sku",
      name: "productCode",
      type: "text",
      value: catalogDetails?.productCode,
      disabled: true,
    },
    ...transformLeftProductDetailSection(
      catalogDetails,
      catalogStatusList,
      catalogInventoryStatusList,
      undefined,
      true,
    ),
  ];
};

export const transformLeftCollectionDetailSection = (
  collectionDetails: ProductDetailsType,
  collectionStatusList: StatusOption[] = [],
) => {
  return [
    {
      label: "Collection Status",
      name: "status",
      type: "select",
      value: convertStatus(collectionDetails && collectionDetails?.status),
      options: collectionStatusList || [],
      disabled: false,
    },
    {
      label: "Product Quantity",
      name: "productQuantity",
      type: "text",
      value: collectionDetails?.products?.length || 0,
      disabled: true,
    },
    {
      label: "Collection Owner",
      name: "collectionOwner",
      type: "text",
      value: collectionDetails?.collectionOwner, // Need to update the owner
      disabled: true,
    },
    {
      label: "Hide collection banner",
      name: "hideCollectionBanner",
      type: "checkbox",
      value: Boolean(collectionDetails?.hideCollectionBanner),
      description: "Banner will not display on the collection page.",
      disabled: false,
    },
  ];
};

export const getInitials = (fullName: string): string => {
  if (!fullName) return "";
  const names = fullName.trim().split(" ");
  const firstInitial = names[0]?.[0] || "";
  const lastInitial = names.length > 1 ? names[names.length - 1][0] : "";
  return `${firstInitial}${lastInitial}`.toUpperCase();
};

export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied!");
  } catch (err) {
    toast.error("Failed to copy.");
    console.error("Copy failed:", err);
  }
};

export const getProductMediaUrl = (
  mediaItems: any[],
  defaultImage: boolean,
  category: string,
): string | { url: string; isDefault: boolean; mediaCategory: string }[] => {
  if (defaultImage) {
    const defaultItem = mediaItems?.find(
      (item) => item.isDefault && item.mediaCategory === category,
    );
    return defaultItem?.url;
  } else {
    return (
      mediaItems
        ?.filter((item) => item.mediaCategory === category)
        .map((item) => ({
          url: item.url,
          isDefault: item.isDefault,
          mediaCategory: item.mediaCategory,
        })) || []
    );
  }
};

export const validateZipCode = (zip: string): string => {
  if (zip.trim().length === 0) return "";
  const zipRegex = /^\d{5}(?:[-\s]\d{4})?$/;
  if (!zip || !zipRegex.test(zip)) {
    return "Please enter a valid ZIP code";
  }
  return "";
};

export const mergeInitialValuesWithDetails = (
  defaultValues: any,
  details: any,
) => {
  const mergedValues = { ...defaultValues };

  for (const key in details) {
    const value = details[key];

    if (productDetailMappings[key]) {
      Object.assign(mergedValues, productDetailMappings[key](value));
    } else {
      mergedValues[key] = value;
    }
  }
  return mergedValues;
};

export const truncateAfterFirstComma = (text: string): string => {
  const [first, ...rest] = text.split(",");
  return rest.length ? `${first},...` : first;
};

export const normalizeType = (
  type: string[] | string | undefined,
): string[] => {
  if (!type) return [];

  if (typeof type === "string") {
    return type.split(",").map((t) => t.trim());
  }

  if (Array.isArray(type)) {
    return type.flatMap((t) =>
      typeof t === "string" ? t.split(",").map((s) => s.trim()) : [],
    );
  }

  return [];
};
